import { useEffect, useState, useCallback } from 'react'

export interface NetworkStatusState {
  isOnline: boolean
  isSlowConnection: boolean
  effectiveType: 'slow-2g' | '2g' | '3g' | '4g' | 'unknown'
  connectionType: 'wifi' | 'cellular' | 'ethernet' | 'unknown'
}

export const useNetworkStatus = () => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatusState>({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    isSlowConnection: false,
    effectiveType: 'unknown',
    connectionType: 'unknown'
  })

  const updateNetworkStatus = useCallback(() => {
    if (typeof navigator === 'undefined') return

    // Check online/offline
    const isOnline = navigator.onLine

    // Get connection info
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection

    let isSlowConnection = false
    let effectiveType: 'slow-2g' | '2g' | '3g' | '4g' | 'unknown' = 'unknown'
    let connectionType: 'wifi' | 'cellular' | 'ethernet' | 'unknown' = 'unknown'

    if (connection) {
      effectiveType = connection.effectiveType || 'unknown'
      connectionType = connection.type || 'unknown'

      // Consider slow if effective type is 2g or 3g, or if save data is enabled
      isSlowConnection =
        (effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g') ||
        connection.saveData === true
    }

    setNetworkStatus({
      isOnline,
      isSlowConnection,
      effectiveType,
      connectionType
    })
  }, [])

  useEffect(() => {
    // Initial check
    updateNetworkStatus()

    // Listen for online/offline events
    window.addEventListener('online', updateNetworkStatus)
    window.addEventListener('offline', updateNetworkStatus)

    // Listen for connection changes
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection

    if (connection) {
      connection.addEventListener('change', updateNetworkStatus)

      return () => {
        window.removeEventListener('online', updateNetworkStatus)
        window.removeEventListener('offline', updateNetworkStatus)
        connection.removeEventListener('change', updateNetworkStatus)
      }
    }

    return () => {
      window.removeEventListener('online', updateNetworkStatus)
      window.removeEventListener('offline', updateNetworkStatus)
    }
  }, [updateNetworkStatus])

  return networkStatus
}
